<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { normalizePhoneNumber } from 'dashboard/components-next/Dialpad/helpers/dialpadHelper';
import WebCallsAPI from 'dashboard/api/webCalls';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const inboxes = useMapGetter('inboxes/getInboxes');
const webInboxes = computed(() =>
  (inboxes.value || []).filter(inbox => inbox.channel_type === INBOX_TYPES.WEB)
);
const inboxOptions = computed(() =>
  webInboxes.value.map(inbox => ({ value: inbox.id, label: inbox.name }))
);

const dialogRef = ref(null);
const selectedInboxId = ref(null);
const customerName = ref('');
const phoneNumber = ref('');
const link = ref('');
const isCreating = ref(false);

const open = () => {
  selectedInboxId.value = webInboxes.value[0]?.id ?? null;
  customerName.value = '';
  phoneNumber.value = '';
  link.value = '';
  dialogRef.value?.open();
};

defineExpose({ open });

const createLink = async () => {
  if (isCreating.value || !selectedInboxId.value) return;

  isCreating.value = true;
  try {
    const { data } = await WebCallsAPI.createCustomerLink({
      inboxId: selectedInboxId.value,
      name: customerName.value.trim(),
      phoneNumber: normalizePhoneNumber(phoneNumber.value),
    });
    link.value = data.url;
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('CALL_HISTORY.CREATE_LINK.ERROR')
    );
  } finally {
    isCreating.value = false;
  }
};

const copyLink = async () => {
  await copyTextToClipboard(link.value);
  useAlert(t('CALL_HISTORY.CREATE_LINK.COPIED'));
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CALL_HISTORY.CREATE_LINK.TITLE')"
    :description="t('CALL_HISTORY.CREATE_LINK.DESCRIPTION')"
    width="md"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div v-if="link" class="flex flex-col gap-3">
      <p
        class="px-3 py-2 mb-0 text-sm break-all rounded-lg select-all bg-n-alpha-1 text-n-slate-12"
      >
        {{ link }}
      </p>
      <p class="mb-0 text-xs text-n-slate-11">
        {{ t('CALL_HISTORY.CREATE_LINK.HINT') }}
      </p>
      <div class="flex gap-2">
        <Button
          :label="t('CALL_HISTORY.CREATE_LINK.COPY')"
          icon="i-lucide-copy"
          class="flex-1"
          type="button"
          @click="copyLink"
        />
        <Button
          :label="t('CALL_HISTORY.CREATE_LINK.ANOTHER')"
          variant="faded"
          color="slate"
          type="button"
          @click="open"
        />
      </div>
    </div>

    <p v-else-if="!webInboxes.length" class="mb-0 text-sm text-n-slate-11">
      {{ t('CALL_HISTORY.CREATE_LINK.NO_INBOX') }}
    </p>

    <div v-else class="flex flex-col gap-4">
      <Select
        v-if="webInboxes.length > 1"
        v-model="selectedInboxId"
        :options="inboxOptions"
        class="w-full"
      />
      <Input
        v-model="customerName"
        :label="t('CALL_HISTORY.CREATE_LINK.NAME_LABEL')"
        :placeholder="t('CALL_HISTORY.CREATE_LINK.NAME_PLACEHOLDER')"
        @enter="createLink"
      />
      <Input
        v-model="phoneNumber"
        type="tel"
        :label="t('CALL_HISTORY.CREATE_LINK.PHONE_LABEL')"
        :placeholder="t('CALL_HISTORY.CREATE_LINK.PHONE_PLACEHOLDER')"
        @enter="createLink"
      />
      <Button
        :label="t('CALL_HISTORY.CREATE_LINK.SUBMIT')"
        :is-loading="isCreating"
        :disabled="isCreating"
        class="w-full"
        type="button"
        @click="createLink"
      />
    </div>
  </Dialog>
</template>
